// Shared layout pieces for app. and admin.
import { Component, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useAuth } from './auth';

export const ROLE_LABELS: Record<string, string> = { AgencyOwner: 'Agency Owner', OfficeManager: 'Office Manager', Employee: 'Employee' };

export function TopBar({ site }: { site: string }) {
  const { user, signOut } = useAuth();
  return (
    <header className="topbar">
      <Link to="/" className="brand" aria-label="Agency Command Center home">
        <span aria-hidden="true">◈</span> Agency Command Center
      </Link>
      <span className="site">{site}</span>
      <span className="spacer" />
      {user && (
        <>
          <span className="who" data-testid="signed-in-as">Signed in as <strong>{user.username}</strong></span>
          <button type="button" className="link" onClick={() => void signOut()}>Sign out</button>
        </>
      )}
    </header>
  );
}

export function SignInScreen({ title, blurb }: { title: string; blurb: string }) {
  const { signIn } = useAuth();
  return (
    <div className="center">
      <div className="hero card">
        <h1>{title}</h1>
        <p className="sub" style={{ marginBottom: 20 }}>{blurb}</p>
        <button type="button" className="btn primary" onClick={() => void signIn()}>Sign in with Microsoft</button>
        <p className="sub" style={{ marginTop: 16, fontSize: 13 }}>Access is by invitation. Use the email address your invitation was sent to.</p>
      </div>
    </div>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="center" role="status" aria-live="polite">
      <div><div className="spinner" /><p className="sub" style={{ marginTop: 12 }}>{label}</p></div>
    </div>
  );
}

export function Banner({ tone, children }: { tone: 'ok' | 'warn' | 'bad'; children: ReactNode }) {
  return <div className={`banner ${tone}`} role={tone === 'bad' ? 'alert' : 'status'}>{children}</div>;
}

export function Badge({ status }: { status: string }) {
  return <span className={`badge ${status}`}>{status}</span>;
}

export const formatDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

/** Last line of defence: a page that throws shows this instead of a blank screen. */
export class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) { console.error('ACC page error', error); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="center">
        <div className="hero card" role="alert">
          <h1>Something went wrong on this page</h1>
          <p className="sub">Your saved work is safe. Reload to try again; if it keeps happening, tell ACC support what you were doing.</p>
          <button type="button" className="btn primary" style={{ marginTop: 12 }} onClick={() => location.reload()}>Reload</button>
        </div>
      </div>
    );
  }
}
